import { useContext, useEffect, useRef, useState } from "react";
import {
  Backdrop,
  Box,
  Button,
  Card,
  CircularProgress,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormLabel,
  Grid2,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
} from "@mui/material";
import { PageContext } from "../contexts/PageContext";
import { ZoomIn, ZoomOut } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { getSecret, loadOrgRecords, saveRecord } from "../api/connection";
import PageHeader from "../components/PageHeader";
import * as atlas from "azure-maps-control";
import { forward, toPoint } from "mgrs";
import { Site } from "../models/Site";
import { getSingleElevation } from "../utils/geo";
import { useSidebarContext } from "../contexts/SideBarContext";

enum MarkerType {
  Site = "Site",
  Robot = "Robot",
}

function calculateBezierPoint(
  t: number,
  start: [number, number],
  control: [number, number],
  end: [number, number]
): [number, number] {
  const x =
    Math.pow(1 - t, 2) * start[0] +
    2 * (1 - t) * t * control[0] +
    Math.pow(t, 2) * end[0];
  const y =
    Math.pow(1 - t, 2) * start[1] +
    2 * (1 - t) * t * control[1] +
    Math.pow(t, 2) * end[1];
  return [x, y];
}

// Function to calculate control points for a quadratic Bézier curve
function calculateBezierControlPoint(
  start: [number, number],
  end: [number, number],
  intensity: number = 0.02
): [number, number] {
  const midpoint = [(start[0] + end[0]) / 2, (start[1] + end[1]) / 2];

  const offset =
    Math.sqrt(Math.pow(end[0] - start[0], 2) + Math.pow(end[1] - start[1], 2)) *
    intensity;

  const controlPoint: [number, number] = [
    midpoint[0] - offset,
    midpoint[1] - offset,
  ];

  return controlPoint;
}

// Function to generate points along a quadratic Bézier curve
function generateBezierCurve(
  start: [number, number],
  control: [number, number],
  end: [number, number],
  segments: number = 20
): [number, number][] {
  const curvePoints: [number, number][] = [];
  for (let i = 0; i <= segments; i++) {
    const t = i / segments; // t is the parameter from 0 to 1
    curvePoints.push(calculateBezierPoint(t, start, control, end));
  }
  return curvePoints;
}

export default function Map() {
  const [loading, setLoading] = useState<boolean>(true);
  const [mapKey, setMapKey] = useState<string>("");
  const [mapClientId, setMapClientId] = useState<string>("");
  const [map, setMap] = useState<atlas.Map | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const [sites, setSites] = useState<Site[]>();
  const [siteCoords, setSiteCoords] = useState<[number, number][]>();
  // const [robots, setRobots] = useState<Robot[]>([]);
  // const [robotCoords, setRobotCoords] = useState<[number, number][]>([]);
  const [selectedMarker, setSelectedMarker] = useState<{
    latLng: [number, number];
    name: string;
    type: string;
  } | null>(null);
  const [openNewSiteDialog, setOpenNewSiteDialog] = useState<boolean>(false);
  const [newSiteProps, setNewSiteProps] = useState<{
    name: string;
    position: { lat: number; long: number; mgrs: string; elevation: number };
  } | null>(null);
  const { isLoggedIn, user } = useContext(PageContext);
  const { isCollapsed } = useSidebarContext();

  const navigate = useNavigate();
  const mapStyle = "grayscale_dark";

  useEffect(() => {
    async function fetchMapKey() {
      const key = await getSecret("MapKey");
      setMapKey(key);

      const clientId = await getSecret("MapClientId");
      setMapClientId(clientId);
    }
    if (isLoggedIn && user) {
      fetchMapKey();
    }
  }, [isLoggedIn, user]);

  useEffect(() => {
    async function fetchSitesAndRobots() {
      try {
        const sitesRes = await loadOrgRecords<Site>("sites");
        // const robotsRes = await loadRecords<Robot>("robots");
        console.log({ sitesRes });
        setSites(sitesRes);
        // setRobots(robotsRes);
        const siteCoords = sitesRes
          .map((site) => {
            return site.location ? toPoint(site.location) : null;
          })
          .filter((coord) => coord) as [number, number][];
        console.log({ siteCoords });
        // const robotCoords = robotsRes.map((robot, i) => {
        //   return robot.location
        //     ? toPoint(robot.location)
        //     : null;
        // }).filter((coord) => coord) as [number, number][];
        // console.log("mine mgrs", forward([16.1361125, 51.4881046]));
        setSiteCoords([
          ...siteCoords,
          // [siteCoords[0][0], -siteCoords[0][1]],
          // [siteCoords[1][0], -siteCoords[1][1]],
          // [-1, -1],
        ]);
        // setRobotCoords(robotCoords);
        setLoading(false);
      } catch (err) {
        console.error("Failed to fetch sites and robots:", err);
      }
    }
    if (isLoggedIn && user) {
      fetchSitesAndRobots();
    }
  }, [isLoggedIn, user]);

  useEffect(() => {
    if (
      !map &&
      // (siteCoords.length || robotCoords.length)
      siteCoords &&
      sites
    ) {
      const mapInstance = new atlas.Map("myMap", {
        view: "Auto",
        style: mapStyle,
        center: [-95.7129, 37.0902], // Center of the US
        authOptions: {
          authType: atlas.AuthenticationType.subscriptionKey,
          subscriptionKey: mapKey,
          clientId: mapClientId,
        },
        domain: "atlas.azure.us",
        enableAccessibility: false,
        showFeedbackLink: false,
        showLogo: false,
      });

      mapInstance.events.add("ready", async () => {
        const dataSource = new atlas.source.DataSource("markersSource");
        mapInstance.sources.add(dataSource);

        const pathSource = new atlas.source.DataSource("pathSource");
        mapInstance.sources.add(pathSource);

        for (let i = 0; i < siteCoords.length; i++) {
          const point = new atlas.data.Point(siteCoords[i]);
          const feature = new atlas.data.Feature(point, {
            id: `Site_${i}`,
            _id: sites[i]?._id || `Site_${i}`,
            name: sites[i]?.name || `Site ${i}`,
            type: MarkerType.Site,
            simId: sites[i]?.simId || "",
          });

          dataSource.add(feature);

          // const pathCoords = (
          //   await getElevation(
          //     sites[i].location,
          //     sites[i].gridSize,
          //     sites[i].cellSize
          //   )
          // ).points.map((point) => [point.lng(), point.lat()]);
          // console.log({ pathCoords });
          // const pathFeature = new atlas.data.Feature(
          //   new atlas.data.LineString(pathCoords),
          //   {
          //     id: `Path_${i}`,
          //     _id: `Path_${i}`,
          //     name: `Path ${i}`,
          //     type: "Path",
          //   }
          // );

          // pathSource.add(pathFeature);
        }

        // for (let i = 0; i < robotCoords.length; i++) {
        //   const point = new atlas.data.Point(robotCoords[i]);
        //   const feature = new atlas.data.Feature(point, {
        //     id: `Robot_${i}`,
        //     _id: robots[i]?._id || `Robot_${i}`,
        //     name: robots[i]?.name || `Robot ${i}`,
        //     type: MarkerType.Robot,
        //   });

        //   dataSource.add(feature);
        // }

        const layer = new atlas.layer.SymbolLayer(dataSource, "markersLayer", {
          iconOptions: {
            image: [
              "case",
              ["==", ["get", "type"], "Site"],
              "marker-red", // Use red for sites
              "marker-blue", // Default to blue for robots
            ],
            allowOverlap: true,
            anchor: "bottom",
            size: 0.6,
          },
        });

        const pathLayer = new atlas.layer.LineLayer(pathSource, "pathLayer", {
          strokeColor: "red",
          strokeWidth: 1,
        });

        mapInstance.layers.add(layer);
        mapInstance.layers.add(pathLayer);

        mapInstance.events.add("click", layer, (e) => {
          console.log("click marker", e);
          if (e.shapes && e.shapes.length > 0) {
            const shape = e.shapes[0] as atlas.Shape;
            const properties = shape.getProperties();
            const { _id, id, name, type, simId } = properties;
            const coordinates = shape.getCoordinates() as [number, number];
            console.log({ _id, id, name, type, coordinates });
            if (type === MarkerType.Site && simId) {
              navigate("/sim/" + simId);
            }

            // if (mapInstance.getCamera().zoom! < 17) {
            //   mapInstance.setCamera({
            //     center: coordinates,
            //     zoom: 17,
            //   });
            // }
          } else {
            console.log("no shapes, position is:", e.position);
          }
        });

        mapInstance.events.add("mouseover", layer, (e) => {
          if (e.shapes && e.shapes.length > 0) {
            const shape = e.shapes[0] as atlas.Shape;
            const properties = shape.getProperties();
            const { _id, id, name, type } = properties;
            const coordinates = shape.getCoordinates() as [number, number];

            setSelectedMarker({
              latLng: [coordinates[1], coordinates[0]],
              name,
              type,
            });
          }
          mapInstance.getCanvas().style.cursor = "pointer";
        });

        mapInstance.events.add("mouseout", layer, (e) => {
          // setSelectedMarker(null);
          mapInstance.getCanvas().style.cursor = "";
        });

        mapInstance.events.add("click", async (e) => {
          setSelectedMarker(null); // Hide text fields when clicking outside a marker
          if (e.shapes && e.shapes.length === 1) {
            console.log({ position: e.position, e });
            const mgrs = forward(e.position as [number, number]);
            const elevation = Math.round(await getSingleElevation(mgrs));
            setNewSiteProps({
              name: "",
              position: {
                lat: e.position![0],
                long: e.position![1],
                mgrs,
                elevation,
              },
            });
            setOpenNewSiteDialog(true);
          }
        });

        // mapInstance.getCanvas().addEventListener("click", (e) => {
        //   setSelectedMarker(null); // Hide text fields when clicking outside a marker
        // });

        mapInstance.getCanvas().addEventListener("keyup", (e) => {
          if (e.key === "Escape") {
            setSelectedMarker(null);
          }
        });
        mapInstance.getCanvas().style.outline = "none";
        mapInstance.getCanvas().focus();
      });

      setMap(mapInstance);
      setLoading(false);
    }
    // }, [siteCoords, robotCoords]);
  }, [sites, siteCoords]);

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />

        {/* page header */}

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 0,
            m: 0,
            backgroundColor: "#212121",
            color: "#ECECEC",
            minHeight: "100vh",
          }}
        >
          <Backdrop
            open={loading}
            sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
          <PageHeader
            title={"Map"}
            // rightComponent={[
            //   <Minibar
            //     minibarItems={[
            //       {
            //         text: "Add Organization",
            //         icon: <GroupAdd />,
            //         nav: `/edit-org/new`,
            //       },
            //     ]}
            //     barLength={1}
            //     searchLabel="Search Organizations"
            //     onSearch={(e) => setFilter(e)}
            //   />,
            // ]}
          />
          <Card
            elevation={0}
            sx={{
              position: "relative",
              border: "none",
              borderRadius: 1,
              borderColor: "divider",
              boxShadow: "inherit",
              ":hover": {
                boxShadow: "inherit",
              },
              width: "100%",
              backgroundImage: "none",
            }}
          >
            <div style={{ height: "calc(100vh - 141px)" }}>
              <Grid2 container height={"calc(100vh - 141px)"}>
                <Grid2
                  position="relative"
                  width={
                    isCollapsed ? `calc(100vw - 72px)` : `calc(100vw - 239.5px)`
                  }
                >
                  {selectedMarker && (
                    <div
                      style={{
                        padding: 5,
                        position: "absolute",
                        top: 5,
                        left: 5,
                        background: "rgb(30,30,30)",
                        opacity: 0.9,
                        display: "flex",
                        flexDirection: "column",
                        gap: 5,
                      }}
                    >
                      <TextField
                        id="markerLat"
                        label="Latitude"
                        value={selectedMarker?.latLng?.[0] || ""}
                        size="small"
                        sx={{
                          m: "0.3rem 0 0.3rem",
                          width: "10rem",
                          "& .MuiOutlinedInput-root": {
                            borderWidth: 1,
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "inherit", // Keeps border unchanged on hover
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "inherit", // Keeps border unchanged on focus
                            },
                          },
                          "& .MuiInputLabel-root": {
                            color: "inherit", // Keeps label color unchanged
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: "inherit", // Keeps label color unchanged on focus
                          },
                        }}
                        slotProps={{
                          input: {
                            readOnly: true,
                            style: { fontSize: "0.6rem" },
                          },
                          inputLabel: { shrink: true },
                        }}
                      />
                      <TextField
                        id="markerLong"
                        label="Longitude"
                        value={selectedMarker?.latLng?.[1] || ""}
                        size="small"
                        sx={{
                          m: "0 0 0.3rem",
                          width: "10rem",
                          "& .MuiOutlinedInput-root": {
                            borderWidth: 1,
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "inherit", // Keeps border unchanged on hover
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "inherit", // Keeps border unchanged on focus
                            },
                          },
                          "& .MuiInputLabel-root": {
                            color: "inherit", // Keeps label color unchanged
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: "inherit", // Keeps label color unchanged on focus
                          },
                        }}
                        slotProps={{
                          input: {
                            readOnly: true,
                            style: { fontSize: "0.6rem" },
                          },
                          inputLabel: { shrink: true },
                        }}
                      />
                      <TextField
                        id="markerName"
                        label="Name"
                        value={selectedMarker?.name || ""}
                        size="small"
                        sx={{
                          m: "0 0 0.3rem",
                          width: "10rem",
                          "& .MuiOutlinedInput-root": {
                            borderWidth: 1,
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "inherit", // Keeps border unchanged on hover
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "inherit", // Keeps border unchanged on focus
                            },
                          },
                          "& .MuiInputLabel-root": {
                            color: "inherit", // Keeps label color unchanged
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: "inherit", // Keeps label color unchanged on focus
                          },
                        }}
                        slotProps={{
                          input: {
                            readOnly: true,
                            style: { fontSize: "0.6rem" },
                          },
                          inputLabel: { shrink: true },
                        }}
                      />
                      <TextField
                        id="markerType"
                        label="Type"
                        value={selectedMarker?.type || ""}
                        size="small"
                        sx={{
                          m: "0 0 0.3rem",
                          width: "10rem",
                          "& .MuiOutlinedInput-root": {
                            borderWidth: 1,
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "inherit", // Keeps border unchanged on hover
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "inherit", // Keeps border unchanged on focus
                            },
                          },
                          "& .MuiInputLabel-root": {
                            color: "inherit", // Keeps label color unchanged
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color: "inherit", // Keeps label color unchanged on focus
                          },
                        }}
                        slotProps={{
                          input: {
                            readOnly: true,
                            style: { fontSize: "0.6rem" },
                          },
                          inputLabel: { shrink: true },
                        }}
                      />
                    </div>
                  )}
                  <IconButton
                    sx={{
                      position: "absolute",
                      top: 5,
                      right: 50,
                      backgroundColor: "rgba(0, 0, 0, 0.7)",
                      color: "white",
                      "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.9)" },
                    }}
                    onClick={() => {
                      if (map) {
                        const currentZoom = map.getCamera().zoom!;
                        map.setCamera({
                          zoom: currentZoom < 20 ? currentZoom + 1 : 20,
                        });
                      }
                    }}
                  >
                    <ZoomIn />
                  </IconButton>
                  <IconButton
                    sx={{
                      position: "absolute",
                      top: 5,
                      right: 5,
                      backgroundColor: "rgba(0, 0, 0, 0.7)",
                      color: "white",
                      "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.9)" },
                    }}
                    onClick={() => {
                      if (map) {
                        const currentZoom = map.getCamera().zoom!;
                        map.setCamera({
                          zoom: currentZoom > 10 ? currentZoom - 1 : 1,
                        });
                      }
                    }}
                  >
                    <ZoomOut />
                  </IconButton>
                  <div
                    id="myMap"
                    style={{
                      height: "calc(100vh - 141px)",
                      marginBottom: "100px",
                    }}
                    ref={mapRef}
                  ></div>
                </Grid2>
              </Grid2>
            </div>
          </Card>
          <Dialog
            open={openNewSiteDialog}
            onClose={() => {
              setOpenNewSiteDialog(false);
              setNewSiteProps(null);
            }}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">
              {"Create New Site"}
            </DialogTitle>
            <DialogContent>
              <TableContainer component={Paper}>
                <Table aria-label="simple table">
                  <TableBody>
                    <TableRow>
                      <TableCell>
                        <FormLabel>Site Name</FormLabel>
                      </TableCell>
                      <TableCell>
                        <TextField
                          id="newSiteName"
                          label="Name"
                          value={newSiteProps?.name || ""}
                          onChange={(e) =>
                            setNewSiteProps({
                              ...newSiteProps!,
                              name: e.target.value,
                            })
                          }
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <FormLabel>Latitude</FormLabel>
                      </TableCell>
                      <TableCell>{newSiteProps?.position.lat}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <FormLabel>Longitude</FormLabel>
                      </TableCell>
                      <TableCell>{newSiteProps?.position.long}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <FormLabel>MGRS</FormLabel>
                      </TableCell>
                      <TableCell>{newSiteProps?.position.mgrs}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <FormLabel>Elevation</FormLabel>
                      </TableCell>
                      <TableCell>{newSiteProps?.position.elevation}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setOpenNewSiteDialog(false);
                  setNewSiteProps(null);
                }}
                color="primary"
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  setLoading(true);
                  let newSite_id = "";
                  try {
                    const newSite: Site = {
                      name: newSiteProps!.name,
                      simId: "",
                      location: newSiteProps!.position.mgrs,
                      gridSize: 200,
                      cellSize: 1,
                      elevation: newSiteProps!.position.elevation,
                      numberOfTiles: 0,
                      orgId: user!.orgId,
                      _id: "",
                    };
                    const res = await saveRecord<Site>("sites", newSite);
                    newSite_id = res._id;
                    console.log("New site:", newSiteProps);
                  } catch (err) {
                    console.error("Failed to add new site:", err);
                  } finally {
                    setLoading(false);
                    setOpenNewSiteDialog(false);
                    setNewSiteProps(null);
                    if (newSite_id) {
                      navigate(`/edit-site/${newSite_id}`);
                    }
                  }
                }}
                color="primary"
                autoFocus
                disabled={loading}
              >
                Confirm
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>
    </>
  );
}
